import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../App';

const API = process.env.REACT_APP_API_URL || 'https://admin.valeaplanning.sbs/api/admin';

const STATUT_CONFIG = {
  'ouvert':            { color: '#B8860B', bg: 'rgba(184,134,11,0.12)', label: 'Ouvert' },
  'à_planifier':       { color: '#B8860B', bg: 'rgba(184,134,11,0.12)', label: 'À planifier' },
  'planifié':          { color: '#185FA5', bg: 'rgba(24,95,165,0.12)',  label: 'Planifié' },
  'en_cours':          { color: '#f97316', bg: 'rgba(249,115,22,0.12)', label: 'En cours' },
  'terminé_SUCCES':    { color: '#1E6B0A', bg: 'rgba(30,107,10,0.12)', label: 'Succès' },
  'terminé_ECHEC':     { color: '#C0392B', bg: 'rgba(192,57,43,0.12)', label: 'Échec' },
  'OT annulé COVAGE':  { color: '#6b7280', bg: 'rgba(107,114,128,0.12)', label: 'Annulé COVAGE' },
  'Standby COVAGE':    { color: '#6C3483', bg: 'rgba(108,52,131,0.12)', label: 'Standby COVAGE' },
  'Standby VALEA':     { color: '#6C3483', bg: 'rgba(108,52,131,0.12)', label: 'Standby VALEA' },
};

function StatutBadge({ statut }) {
  if (!statut) return <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>;
  const cfg = STATUT_CONFIG[statut] || { color: '#8b92a5', bg: 'rgba(139,146,165,0.12)', label: statut };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}44`,
      whiteSpace: 'nowrap',
    }}>{cfg.label}</span>
  );
}

function FactBadge({ facturation }) {
  if (!facturation) return null;
  const color = facturation.includes('96') ? '#C0392B' : '#A0197F';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
      color, background: color + '18', border: `1px solid ${color}44`,
    }}>{facturation}</span>
  );
}

function CommentsSection({ recordId, token }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState('');
  const [text, setText]         = useState('');
  const [posting, setPosting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API}/sav/${recordId}/comments`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (r.ok) setComments(d.comments || []);
      else setErr(d.code === 'INVALID_PERMISSIONS_OR_MODEL_NOT_FOUND'
        ? 'Le token Airtable n\'a pas le scope "recordComments". À ajouter dans les paramètres du token.'
        : (d.error || 'Erreur'));
    } catch { setErr('Erreur réseau'); }
    finally { setLoading(false); }
  }, [recordId, token]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    const t = text.trim();
    if (!t) return;
    setPosting(true); setErr('');
    try {
      const r = await fetch(`${API}/sav/${recordId}/comments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: t }),
      });
      const d = await r.json();
      if (r.ok && d.success) { setText(''); await load(); }
      else setErr(d.error || 'Erreur envoi');
    } catch { setErr('Erreur réseau'); }
    finally { setPosting(false); }
  };

  const fmtDate = iso => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginTop: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
        Commentaires Airtable {comments.length > 0 && `(${comments.length})`}
      </div>

      {loading && <div style={{ fontSize: 12, color: '#888' }}>Chargement…</div>}
      {err && <div style={{ fontSize: 12, color: '#991b1b', background: '#fee2e2', padding: '8px 12px', borderRadius: 8, marginBottom: 10 }}>{err}</div>}

      {!loading && !err && comments.length === 0 && (
        <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic', marginBottom: 10 }}>Aucun commentaire</div>
      )}

      {comments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, maxHeight: 260, overflowY: 'auto' }}>
          {comments.map(c => (
            <div key={c.id} style={{ background: '#f7f8fa', borderRadius: 8, padding: '9px 12px', border: '1px solid #eceef2' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#185FA5' }}>{c.author || 'Inconnu'}</span>
                <span style={{ fontSize: 10, color: '#999', whiteSpace: 'nowrap' }}>{fmtDate(c.created_time)}{c.edited ? ' (modifié)' : ''}</span>
              </div>
              <div style={{ fontSize: 12, color: '#333', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{c.text}</div>
            </div>
          ))}
        </div>
      )}

      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Écrire un commentaire…"
        rows={3}
        style={{
          width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1px solid #d5d9e0',
          padding: '9px 12px', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', color: '#1a1a2e',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button
          onClick={post}
          disabled={posting || !text.trim()}
          style={{
            padding: '7px 16px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 700,
            background: '#185FA5', color: '#fff',
            cursor: (posting || !text.trim()) ? 'not-allowed' : 'pointer',
            opacity: (posting || !text.trim()) ? 0.5 : 1,
          }}
        >
          {posting ? 'Envoi…' : 'Publier'}
        </button>
      </div>
    </div>
  );
}

function DetailModal({ sav, onClose, onExported, token, zohoId }) {
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null); // {ok, message}

  const canExport = sav.statut === 'à_planifier';

  const handleExport = async () => {
    if (!window.confirm(`Exporter "${sav.wo_reference}" vers Zoho CRM ?`)) return;
    setExporting(true); setExportResult(null);
    try {
      const r = await fetch(`${API}/sav/export-zoho/${sav.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (r.ok && data.success) {
        setExportResult({ ok: true, message: `Créé dans Zoho — ID ${data.zoho_id}` });
        if (onExported) onExported(sav.id, data.zoho_id);
      } else {
        setExportResult({ ok: false, message: data.error || 'Erreur inconnue' });
      }
    } catch (e) {
      setExportResult({ ok: false, message: e.message });
    } finally {
      setExporting(false);
    }
  };

  if (!sav) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 28, maxWidth: 680, width: '100%',
        maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        color: '#1a1a2e',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4, color: '#1a1a2e' }}>{sav.wo_reference}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatutBadge statut={sav.statut} />
              <FactBadge facturation={sav.facturation} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
            {zohoId && (
              <button
                onClick={() => window.open('/dossier/' + zohoId, '_blank', 'noopener')}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: '#fff', color: '#185FA5', border: '1px solid rgba(24,95,165,0.45)',
                }}
              >
                📁 Fiche dossier ↗
              </button>
            )}
            {canExport && (
              <button
                onClick={handleExport}
                disabled={exporting || exportResult?.ok}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: exporting ? 'wait' : 'pointer',
                  background: exportResult?.ok ? '#1E6B0A' : '#185FA5',
                  color: '#fff', border: 'none', opacity: (exporting || exportResult?.ok) ? 0.7 : 1,
                }}
              >
                {exporting ? '⏳ Export…' : exportResult?.ok ? '✓ Exporté' : '🚀 Exporter vers Zoho'}
              </button>
            )}
          </div>
        </div>

        {exportResult && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 12, fontWeight: 600,
            background: exportResult.ok ? '#d1fae5' : '#fee2e2',
            color: exportResult.ok ? '#065f46' : '#991b1b',
          }}>
            {exportResult.ok ? '✓' : '✗'} {exportResult.message}
          </div>
        )}

        {/* Bloc client — infos prioritaires */}
        <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
          {[
            ['Raison sociale', sav.raison_sociale],
            ['Contact client', sav.contact_client],
            ['Téléphone', sav.tel_client],
            ['Disponibilités client', sav.disponibilites],
            ['Motif déclenchement', sav.motif],
          ].map(([lbl, val]) => val ? (
            <div key={lbl} style={lbl === 'Disponibilités client' || lbl === 'Motif déclenchement' ? { gridColumn: '1 / -1' } : {}}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#5065a0', textTransform: 'uppercase', letterSpacing: '.06em' }}>{lbl}</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2, color: '#1a1a2e' }}>{val}</div>
            </div>
          ) : null)}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', marginBottom: 16 }}>
          {[
            ['Réf Covage', sav.covage_order_ref],
            ['Type intervention', sav.type_itv],
            ['Type offre', sav.type_offre],
            ['Date planifiée', sav.date_planifiee ? `${sav.date_planifiee}${sav.heure_planifiee ? ' à ' + sav.heure_planifiee : ''}` : '—'],
            ['Date réelle', sav.date_reelle || '—'],
            ['Date déclenchement', sav.date_declenchement || '—'],
            ['Responsabilité', sav.responsabilite || '—'],
            ['Changement ONT', sav.changt_ont || '—'],
            ['SLID ONT', sav.slid_ont || '—'],
            ['Référence PTO', sav.ref_pto || '—'],
            ['Porteur création', sav.porteur_creation || '—'],
          ].map(([lbl, val]) => (
            <div key={lbl}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '.06em' }}>{lbl}</div>
              <div style={{ fontSize: 13, fontWeight: 500, marginTop: 2, color: '#1a1a2e' }}>{val}</div>
            </div>
          ))}
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Adresse</div>
          <div style={{ fontSize: 13, color: '#1a1a2e' }}>{[sav.adresse, sav.code_postal, sav.ville].filter(Boolean).join(' — ')}</div>
        </div>

        {sav.commentaire_ouverture && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Commentaire ouverture</div>
            <div style={{ fontSize: 12, color: '#444', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{sav.commentaire_ouverture}</div>
          </div>
        )}

        {sav.commentaire_cloture && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Commentaire clôture</div>
            <div style={{ fontSize: 12, color: '#444', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{sav.commentaire_cloture}</div>
          </div>
        )}

        <CommentsSection recordId={sav.id} token={token} />
      </div>
    </div>
  );
}

export default function SAV() {
  const { admin } = useAuth();
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [statuts, setStatuts]   = useState(new Set(['à_planifier']));
  const [textSearch, setTextSearch] = useState('');
  const [detail, setDetail]     = useState(null);
  const [sortCol, setSortCol]   = useState('date_declenchement');
  const [sortDir, setSortDir]   = useState('desc');
  // Zoho statuts par wo_reference : { [wo]: { loading, found, planifie, date, heure, technicien } }
  const [zohoStatuts, setZohoStatuts] = useState({});
  // Sync Airtable en cours par airtable id
  const [syncing, setSyncing]   = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing]   = useState(false);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError('');
    try {
      const r = await fetch(`${API}/sav`, { headers: { Authorization: `Bearer ${admin.token}` } });
      const d = await r.json();
      const list = d.sav || [];
      setData(list);
      setLastRefresh(new Date());
      setZohoStatuts({});
      // Auto-check Zoho pour tous les SAV "à_planifier"
      const aPlanifier = list.filter(s => s.statut === 'à_planifier');
      for (const s of aPlanifier) {
        setZohoStatuts(prev => ({ ...prev, [s.wo_reference]: { loading: true } }));
        try {
          const zr = await fetch(`${API}/sav/zoho-check/${encodeURIComponent(s.wo_reference)}`, {
            headers: { Authorization: `Bearer ${admin.token}` },
          });
          const zd = await zr.json();
          setZohoStatuts(prev => ({ ...prev, [s.wo_reference]: { loading: false, ...zd } }));
        } catch {
          setZohoStatuts(prev => ({ ...prev, [s.wo_reference]: { loading: false, error: true } }));
        }
      }
    } catch {
      setError('Erreur chargement SAV');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [admin.token]);

  // Chargement initial
  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh toutes les 5 minutes
  useEffect(() => {
    const interval = setInterval(() => loadData(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData]);

  const allStatuts = useMemo(() => {
    const all = [...new Set(data.map(s => s.statut).filter(Boolean))];
    // "à_planifier" toujours en premier
    return all.sort((a, b) => {
      if (a === 'à_planifier') return -1;
      if (b === 'à_planifier') return 1;
      return a.localeCompare(b);
    });
  }, [data]);

  const filtered = useMemo(() => {
    const q = textSearch.trim().toLowerCase();
    return data.filter(s => {
      if (statuts.size > 0 && !statuts.has(s.statut)) return false;
      if (q && !`${s.wo_reference} ${s.covage_order_ref} ${s.ville} ${s.adresse} ${s.type_itv} ${s.motif}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, statuts, textSearch]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const av = (a[sortCol] || '').toString();
    const bv = (b[sortCol] || '').toString();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  }), [filtered, sortCol, sortDir]);

  const checkZoho = useCallback(async (woRef) => {
    setZohoStatuts(prev => ({ ...prev, [woRef]: { loading: true } }));
    try {
      const r = await fetch(`${API}/sav/zoho-check/${encodeURIComponent(woRef)}`, {
        headers: { Authorization: `Bearer ${admin.token}` },
      });
      const d = await r.json();
      setZohoStatuts(prev => ({ ...prev, [woRef]: { loading: false, ...d } }));
    } catch {
      setZohoStatuts(prev => ({ ...prev, [woRef]: { loading: false, error: true } }));
    }
  }, [admin.token]);

  const syncAirtable = useCallback(async (airtableId, woRef, date, heure) => {
    if (!window.confirm(`Mettre à jour Airtable pour ${woRef} ?\n\nStatut → planifié\nDate → ${date} ${heure}`)) return;
    setSyncing(prev => ({ ...prev, [airtableId]: true }));
    try {
      const r = await fetch(`${API}/sav/sync-airtable/${airtableId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${admin.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, heure }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        // Mettre à jour la ligne localement
        setData(prev => prev.map(s => s.id === airtableId
          ? { ...s, statut: 'planifié', date_planifiee: date, heure_planifiee: heure }
          : s
        ));
        setZohoStatuts(prev => ({ ...prev, [woRef]: { ...prev[woRef], synced: true } }));
      } else {
        alert('Erreur sync Airtable : ' + (d.error || 'Erreur inconnue'));
      }
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSyncing(prev => ({ ...prev, [airtableId]: false }));
    }
  }, [admin.token]);

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const SortIcon = ({ col }) => sortCol !== col
    ? <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>
    : <span style={{ marginLeft: 3 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;

  // Compteurs par statut
  const countByStatut = useMemo(() => {
    const c = {};
    data.forEach(s => { c[s.statut] = (c[s.statut] || 0) + 1; });
    return c;
  }, [data]);

  const exportCSV = () => {
    const cols = [
      ['Réf WO', 'wo_reference'], ['Réf Covage', 'covage_order_ref'], ['Statut', 'statut'],
      ['Type ITV', 'type_itv'], ['Facturation', 'facturation'], ['Motif', 'motif'],
      ['Adresse', 'adresse'], ['CP', 'code_postal'], ['Ville', 'ville'],
      ['Date planifiée', 'date_planifiee'], ['Date réelle', 'date_reelle'],
      ['Date déclenchement', 'date_declenchement'], ['SLID ONT', 'slid_ont'],
      ['Réf PTO', 'ref_pto'], ['Responsabilité', 'responsabilite'],
    ];
    const header = cols.map(([l]) => `"${l}"`).join(';');
    const rows = sorted.map(s => cols.map(([, k]) => `"${(s[k] || '').toString().replace(/"/g, '""')}"`).join(';'));
    const csv = '\uFEFF' + [header, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'sav_covage.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">SAV Covage</div>
        {!loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 13, fontWeight: 700, padding: '3px 12px', borderRadius: 20,
              background: sorted.length > 0 ? 'var(--accent)' : 'var(--bg3)',
              color: sorted.length > 0 ? '#fff' : 'var(--text3)',
            }}>{sorted.length} SAV</span>
            {sorted.length !== data.length && <span style={{ fontSize: 11, color: 'var(--text3)' }}>sur {data.length}</span>}
          </div>
        )}
        {lastRefresh && (
          <span style={{ fontSize: 11, color: 'var(--text3)', alignSelf: 'center' }}>
            Actualisé à {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => loadData(true)}
          disabled={refreshing || loading}
          title="Rafraîchir"
        >
          {refreshing ? '⏳' : '↻'} Rafraîchir
        </button>
        {sorted.length > 0 && <button className="btn btn-secondary btn-sm" onClick={exportCSV}>⬇ Export CSV</button>}
      </div>

      <div className="page">
        {/* Cartes statuts */}
        {!loading && allStatuts.length > 0 && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            {allStatuts.map(st => {
              const cfg = STATUT_CONFIG[st] || { color: '#8b92a5', bg: 'rgba(139,146,165,0.12)', label: st };
              const active = statuts.has(st);
              return (
                <button key={st} onClick={() => {
                  const s = new Set(statuts);
                  s.has(st) ? s.delete(st) : s.add(st);
                  setStatuts(s);
                }} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                  borderRadius: 10, border: `2px solid ${active ? cfg.color : 'var(--border)'}`,
                  background: active ? cfg.bg : 'var(--bg2)', cursor: 'pointer',
                  transition: 'all .15s',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: cfg.color }}>{countByStatut[st] || 0}</div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: cfg.color }}>{cfg.label}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Barre de recherche */}
        <div className="card" style={{ marginBottom: 16, padding: '14px 18px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              className="form-input" style={{ flex: 1 }}
              placeholder="Rechercher par réf, ville, type intervention, motif…"
              value={textSearch} onChange={e => setTextSearch(e.target.value)}
            />
            {(statuts.size > 0 || textSearch) && (
              <button className="btn btn-secondary btn-sm" onClick={() => { setStatuts(new Set()); setTextSearch(''); }}>
                ✕ Réinitialiser
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
            Chargement des SAV Airtable…
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}

        {!loading && sorted.length === 0 && !error && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)', fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
            Aucun SAV ne correspond aux critères
          </div>
        )}

        {sorted.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('date_declenchement')}>Déclenchement <SortIcon col="date_declenchement" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('wo_reference')}>Réf WO <SortIcon col="wo_reference" /></th>
                    <th>Réf Covage</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('statut')}>Statut <SortIcon col="statut" /></th>
                    <th>Type ITV</th>
                    <th>Motif</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('ville')}>CP / Ville <SortIcon col="ville" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('date_planifiee')}>Date Airtable <SortIcon col="date_planifiee" /></th>
                    <th>Étape Zoho</th>
                    <th>Date Zoho</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((s, idx) => {
                    const zs = zohoStatuts[s.wo_reference];
                    // Le dossier Zoho de ce SAV : celui que le controle vient
                    // de trouver, ou celui que nous avons exporte nous-memes.
                    const zohoId = (zs && zs.found && zs.zoho_id) || s.zoho_exported_id || null;
                    return (
                    <tr key={s.id || idx} style={{ cursor: 'pointer' }} onClick={() => setDetail(s)}>
                      <td><span style={{ fontSize: 12, color: 'var(--text3)' }}>{s.date_declenchement || '—'}</span></td>
                      <td><span className="mono" style={{ fontSize: 11, color: 'var(--blue2)', fontWeight: 700 }}>{s.wo_reference}</span></td>
                      <td><span className="mono" style={{ fontSize: 11, color: 'var(--text2)' }}>{s.covage_order_ref || '—'}</span></td>
                      <td><StatutBadge statut={s.statut} /></td>
                      <td style={{ fontSize: 11, maxWidth: 160, color: 'var(--text2)' }}>{s.type_itv || '—'}</td>
                      <td style={{ fontSize: 11, color: 'var(--text2)', maxWidth: 140 }}>{s.motif || '—'}</td>
                      <td style={{ fontSize: 12 }}>{s.code_postal ? <span style={{ color: 'var(--text3)', marginRight: 4 }}>{s.code_postal}</span> : ''}{s.ville || '—'}</td>
                      <td>
                        {s.date_planifiee
                          ? <span style={{ fontSize: 12, color: '#185FA5', fontWeight: 600 }}>{s.date_planifiee}{s.heure_planifiee ? ' ' + s.heure_planifiee : ''}</span>
                          : <span style={{ fontSize: 11, color: '#B8860B', fontWeight: 600 }}>À planifier</span>}
                      </td>

                      {/* Colonne Étape Zoho */}
                      <td onClick={e => e.stopPropagation()}>
                        {s.statut === 'à_planifier' && (
                          <>
                            {zs?.loading && <span style={{ fontSize: 10, color: 'var(--text3)' }}>⏳</span>}
                            {zs?.error && <span style={{ fontSize: 10, color: '#C0392B' }}>Erreur</span>}
                            {zs && !zs.loading && !zs.found && <span style={{ fontSize: 10, color: 'var(--text3)' }}>—</span>}
                            {zs?.found && (
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                                color: zs.planifie ? '#185FA5' : '#B8860B',
                                background: zs.planifie ? 'rgba(24,95,165,0.12)' : 'rgba(184,134,11,0.12)',
                                border: `1px solid ${zs.planifie ? '#185FA5' : '#B8860B'}44`,
                              }}>{zs.stage}</span>
                            )}
                            {zs?.synced && <span style={{ fontSize: 10, fontWeight: 700, color: '#1E6B0A' }}>✓ Synced</span>}
                          </>
                        )}
                      </td>

                      {/* Colonne Date Zoho + bouton sync */}
                      <td onClick={e => e.stopPropagation()} style={{ minWidth: 150 }}>
                        {s.statut === 'à_planifier' && zs?.found && zs?.date && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#185FA5' }}>
                              {zs.date}{zs.heure ? ' ' + zs.heure : ''}
                            </span>
                            {zs.technicien && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{zs.technicien}</span>}
                            {zs.planifie && !zs.synced && (
                              <button
                                className="btn btn-sm"
                                disabled={syncing[s.id]}
                                onClick={() => syncAirtable(s.id, s.wo_reference, zs.date, zs.heure)}
                                style={{ background: '#1E6B0A', color: '#fff', border: 'none', fontSize: 10, fontWeight: 700, marginTop: 2, padding: '3px 8px' }}
                              >
                                {syncing[s.id] ? '⏳…' : '✓ Sync → Airtable'}
                              </button>
                            )}
                          </div>
                        )}
                      </td>

                      <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        {/* La fiche d'abord : c'est la notre, elle porte
                            l'historique des etapes, les pieces et les
                            actions. Zoho reste a cote tant que la bascule n'a
                            pas eu lieu. L'identifiant vient du controle Zoho
                            de la ligne, ou de notre propre export. */}
                        {zohoId && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => window.open('/dossier/' + zohoId, '_blank', 'noopener')}
                            style={{ marginRight: 6 }}
                          >
                            📁 Fiche ↗
                          </button>
                        )}
                        {zohoId && (
                          <a
                            href={`https://crmplus.zoho.eu/valeatelecom/index.do/cxapp/crm/org20082707825/tab/Potentials/${zohoId}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, background: 'rgba(24,95,165,0.1)', border: '1px solid rgba(24,95,165,0.3)', color: '#185FA5', fontSize: 11, fontWeight: 700, textDecoration: 'none', marginRight: 6 }}
                          >
                            ↗ Zoho
                          </a>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={() => setDetail(s)} style={{ marginRight: 6 }}>
                          Détail
                        </button>
                        {s.statut === 'à_planifier' && !s.zoho_exported_id && (
                          <button className="btn btn-sm" onClick={() => setDetail(s)}
                            style={{ background: '#185FA5', color: '#fff', border: 'none', fontSize: 11, fontWeight: 700 }}>
                            🚀 Zoho
                          </button>
                        )}
                        {s.zoho_exported_id && (
                          <span style={{ fontSize: 10, color: '#1E6B0A', fontWeight: 700 }}>✓ Exporté</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {detail && (
        <DetailModal
          sav={detail}
          onClose={() => setDetail(null)}
          token={admin.token}
          zohoId={(zohoStatuts[detail.wo_reference] || {}).zoho_id || detail.zoho_exported_id || null}
          onExported={(airtableId, zohoId) => {
            // Marquer localement comme exporté
            setData(prev => prev.map(s => s.id === airtableId ? { ...s, zoho_exported_id: zohoId } : s));
          }}
        />
      )}
    </>
  );
}
